import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    useChecklist,
    useCompleteStep,
    useBlockStep,
    useCancelChecklist,
} from '../hooks';
import { ProgressBar } from '../components/ProgressBar';
import { StepCard } from '../components/StepCard';
import { Button } from '@/components/ui/button';

export function ChecklistDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: checklist, isLoading } = useChecklist(id!);
    const completeMutation = useCompleteStep(id!);
    const blockMutation = useBlockStep(id!);
    const cancelMutation = useCancelChecklist();

    if (isLoading) {
        return <p className="p-6 text-muted-foreground">Loading…</p>;
    }

    if (!checklist) {
        return (
            <div className="p-6 text-center">
                <p className="text-muted-foreground">Checklist not found</p>
                <Link to="/checklists" className="text-primary hover:underline">
                    Back to Checklists
                </Link>
            </div>
        );
    }

    const statusBadge = {
        active: 'bg-blue-50 text-blue-700',
        completed: 'bg-emerald-50 text-emerald-700',
        cancelled: 'bg-gray-100 text-gray-500',
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <Link
                        to="/checklists"
                        className="text-sm text-muted-foreground hover:underline"
                    >
                        ← Checklists
                    </Link>
                    <h1 className="mt-2 text-2xl font-bold">
                        {checklist.template_name}
                    </h1>
                    <div className="mt-2 flex items-center gap-3">
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge[checklist.status]}`}
                        >
                            {checklist.status}
                        </span>
                        <span className="text-sm text-muted-foreground">
                            Started{' '}
                            {new Date(checklist.start_date).toLocaleDateString('en-GB')}
                        </span>
                    </div>
                </div>
                {checklist.status === 'active' && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500"
                        onClick={() => {
                            if (confirm('Cancel this checklist?')) {
                                cancelMutation.mutate(id!, {
                                    onSuccess: () => navigate('/checklists'),
                                });
                            }
                        }}
                    >
                        Cancel
                    </Button>
                )}
            </div>

            {/* Progress */}
            {checklist.progress && <ProgressBar progress={checklist.progress} />}

            {/* Steps */}
            <div className="space-y-3">
                <h2 className="text-lg font-semibold">Steps</h2>
                {checklist.steps?.map((step) => (
                    <StepCard
                        key={step.id}
                        step={step}
                        onComplete={() => completeMutation.mutate(step.id)}
                        onBlock={() => {
                            const reason = prompt('Reason for blocking:');
                            if (reason) {
                                blockMutation.mutate({ stepId: step.id, reason });
                            }
                        }}
                        isCompleting={completeMutation.isPending}
                    />
                ))}
                {!checklist.steps?.length && (
                    <p className="text-sm text-muted-foreground">No steps found.</p>
                )}
            </div>
        </div>
    );
}